import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { Users, Download, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/apiClient';
import { Button, Badge, ConfirmDialog, Input, Modal, PageHeader, EmptyState, Spinner } from '../components/ui';
import { RegistrantImportPanel } from '../components/RegistrantImportPanel';

interface Attendee {
  id: string;
  name: string;
  email: string;
  title: string | null;
  company: string | null;
  role: string;
  registered_at?: string;
  ticket_type?: string;
  reg_status?: string;
  account_status?: string;
}

function roleBadge(role: string) {
  const map: Record<string, 'default' | 'info' | 'purple' | 'warning' | 'success'> = {
    attendee:  'default',
    speaker:   'info',
    vendor:    'warning',
  };
  return <Badge variant={map[role] ?? 'default'}>{role}</Badge>;
}

function accountStatusBadge(status?: string) {
  const labels: Record<string, string> = { active: 'Active', awaiting_activation: 'Awaiting activation', activation_email_sent: 'Activation email sent', email_delivery_failed: 'Email delivery failed', inactive: 'Inactive' };
  const variants: Record<string, 'default' | 'info' | 'warning' | 'success'> = { active: 'success', activation_email_sent: 'info', email_delivery_failed: 'warning', inactive: 'default' };
  return <Badge variant={variants[status ?? ''] ?? 'warning'}>{labels[status ?? ''] ?? 'Awaiting activation'}</Badge>;
}

function AttendeeModal({ open, onClose, attendee, eventId, onSaved }: { open: boolean; onClose: () => void; attendee: Attendee | null; eventId: string; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [pendingSave, setPendingSave] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && attendee) {
      setName(attendee.name); setEmail(attendee.email); setTitle(attendee.title ?? ''); setCompany(attendee.company ?? '');
    }
  }, [open, attendee]);

  async function save() {
    if (!attendee) return;
    setSaving(true);
    try {
      await apiClient.patch(`/admin/attendees/${attendee.id}?event_id=${eventId}`, { name, email, title: title || null, company: company || null });
      toast.success('Attendee profile updated');
      setPendingSave(false); onSaved(); onClose();
    } catch (error) {
      toast.error('Update failed', { description: error instanceof Error ? error.message : 'Unknown error' });
    } finally { setSaving(false); }
  }

  return <Modal open={open} onClose={onClose} title="Edit attendee profile" description="These profile changes apply anywhere this person uses Rubikoo." size="md">
    <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); setPendingSave(true); }}>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Full name *" value={name} onChange={(event) => setName(event.target.value)} required />
        <Input label="Email *" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
        <Input label="Organization" value={company} onChange={(event) => setCompany(event.target.value)} />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit">Save changes</Button>
      </div>
    </form>
    <ConfirmDialog open={pendingSave} onClose={() => setPendingSave(false)} onConfirm={save} loading={saving} intent="primary" title="Save changes?" description="Apply the attendee profile changes?" confirmLabel="Save changes" />
  </Modal>;
}

export default function AttendeesPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [eventName, setEventName] = useState('');
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [editing, setEditing] = useState<Attendee | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Attendee | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [statusTarget, setStatusTarget] = useState<Attendee | null>(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [activationTarget, setActivationTarget] = useState<Attendee | null>(null);
  const [sendingActivation, setSendingActivation] = useState(false);

  async function load() {
    if (!eventId) return;
    setLoading(true);
    try {
      const [eventData, attendeesData] = await Promise.all([
        apiClient.get<{ id: string; name: string }>(`/admin/events/${eventId}`),
        apiClient.get<Attendee[]>(`/admin/attendees?event_id=${eventId}`),
      ]);
      setEventName(eventData.name);
      setAttendees(attendeesData);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [eventId]);

  function exportCSV() {
    const header = 'Name,Email,Title,Company,Role,Ticket,Registered At';
    const rows = filtered.map((a) =>
      [a.name, a.email, a.title ?? '', a.company ?? '', a.role, a.ticket_type ?? '',
       a.registered_at ? new Date(a.registered_at).toLocaleDateString() : ''].map((v) => `"${v}"`).join(',')
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `attendees-${eventId}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} attendees`);
  }

  async function removeAttendee() {
    if (!eventId || !deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/admin/attendees/${deleteTarget.id}?event_id=${eventId}`);
      toast.success('Attendee removed from this event');
      setDeleteTarget(null); load();
    } catch (error) {
      toast.error('Removal failed', { description: error instanceof Error ? error.message : 'Unknown error' });
    } finally { setDeleting(false); }
  }

  async function updateAttendeeStatus() {
    if (!eventId || !statusTarget) return;
    const status = statusTarget.reg_status === 'inactive' ? 'confirmed' : 'inactive';
    setSavingStatus(true);
    try {
      await apiClient.patch(`/admin/attendees/${statusTarget.id}/status?event_id=${eventId}`, { status });
      toast.success(status === 'inactive' ? 'Attendee deactivated for this event' : 'Attendee reactivated for this event');
      setStatusTarget(null); load();
    } catch (error) {
      toast.error('Status update failed', { description: error instanceof Error ? error.message : 'Unknown error' });
    } finally { setSavingStatus(false); }
  }

  async function sendActivationEmail() {
    if (!eventId || !activationTarget) return;
    setSendingActivation(true);
    try {
      await apiClient.post(`/admin/events/${eventId}/activation-jobs/attendees/${activationTarget.id}`, {}, { 'Idempotency-Key': crypto.randomUUID() });
      toast.success('Activation email sent');
      setActivationTarget(null); load();
    } catch (error) {
      toast.error('Activation email failed', { description: error instanceof Error ? error.message : 'Unknown error' });
    } finally { setSendingActivation(false); }
  }

  const filtered = search
    ? attendees.filter((a) =>
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        a.email.toLowerCase().includes(search.toLowerCase()) ||
        a.company?.toLowerCase().includes(search.toLowerCase())
      )
    : attendees;

  return (
    <div className="p-8">
      <PageHeader
        title="Attendees"
        description={`${attendees.length} registered attendees for ${eventName || '…'}`}
        breadcrumb={<Link to="/events" className="hover:text-gray-300">Events</Link>}
        action={
          <Button variant="secondary" icon={<Download size={14} />} onClick={exportCSV} disabled={filtered.length === 0}>
            Export CSV
          </Button>
        }
      />

      {eventId && <RegistrantImportPanel eventId={eventId} onConfirmed={load} />}

      {/* Search + stats */}
      <div className="flex items-center gap-4 mb-5">
        <input
          type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, company…"
          className="w-full max-w-sm px-3.5 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500"
        />
        <span className="text-xs text-gray-500 flex-shrink-0">{filtered.length} of {attendees.length}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner className="w-6 h-6 text-gray-500" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Users} title="No attendees found"
          description="Attendees are registered via the mobile app or event registration flow." />
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Account status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Registered</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filtered.map((a) => (
                <tr key={a.id} className="hover:bg-gray-800/40 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-white text-sm">{a.name}</p>
                      {a.title && <p className="text-xs text-gray-500">{a.title}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-xs">{a.email}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{a.company ?? '—'}</td>
                  <td className="px-4 py-3">{accountStatusBadge(a.account_status)}</td>
                  <td className="px-4 py-3">{roleBadge(a.role)}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {a.registered_at ? new Date(a.registered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="sm" icon={<Pencil size={12} />} aria-label={`Edit ${a.name}`} onClick={() => setEditing(a)} />
                      {['awaiting_activation', 'activation_email_sent', 'email_delivery_failed'].includes(a.account_status ?? '') && <Button variant="ghost" size="sm" aria-label={`${a.account_status === 'activation_email_sent' ? 'Resend' : 'Send'} activation email to ${a.name}`} onClick={() => setActivationTarget(a)}>{a.account_status === 'activation_email_sent' ? 'Resend email' : 'Send email'}</Button>}
                      <Button variant="ghost" size="sm" aria-label={`${a.reg_status === 'inactive' ? 'Activate' : 'Deactivate'} ${a.name}`} onClick={() => setStatusTarget(a)}>{a.reg_status === 'inactive' ? 'Activate' : 'Deactivate'}</Button>
                      <Button variant="ghost" size="sm" icon={<Trash2 size={12} />} aria-label={`Remove ${a.name} from this event`} onClick={() => setDeleteTarget(a)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AttendeeModal open={!!editing} onClose={() => setEditing(null)} attendee={editing} eventId={eventId ?? ''} onSaved={load} />
      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={removeAttendee} loading={deleting}
        title="Remove attendee?" description="This removes the attendee from this event only. Their Rubikoo account remains available for other events." confirmLabel="Remove" />
      <ConfirmDialog open={!!statusTarget} onClose={() => setStatusTarget(null)} onConfirm={updateAttendeeStatus} loading={savingStatus}
        intent="primary" title={statusTarget?.reg_status === 'inactive' ? 'Reactivate attendee?' : 'Deactivate attendee?'} description={statusTarget?.reg_status === 'inactive' ? 'Restore this attendee’s access to this event.' : 'This blocks event access and activation emails, but keeps their account.'} confirmLabel={statusTarget?.reg_status === 'inactive' ? 'Reactivate' : 'Deactivate'} />
      <ConfirmDialog open={!!activationTarget} onClose={() => setActivationTarget(null)} onConfirm={sendActivationEmail} loading={sendingActivation}
        intent="primary" title={activationTarget?.account_status === 'activation_email_sent' ? 'Resend activation email?' : 'Send activation email?'} description={`Send a fresh 72-hour activation link to ${activationTarget?.email ?? ''}.`} confirmLabel={activationTarget?.account_status === 'activation_email_sent' ? 'Resend email' : 'Send email'} />
    </div>
  );
}
